import { ButtonBase } from '@mui/material';
import { alpha, keyframes, styled } from '@mui/material/styles';
import type { NavigationActionContentPosition } from 'components/ui/navigationActionFeedback';

// The chip's own line box: the label slots slide by exactly this much.
const CHIP_LINE_HEIGHT_PX = 12;

// Same glow-and-breathe as the tertiary navigation action buttons' saving state,
// scaled down for an 18px chip.
const chipSavePulse = keyframes`
  0%, 100% { box-shadow: 0 0 6px 0 var(--review-chip-glow-color); }
  50% { box-shadow: 0 0 12px 1px var(--review-chip-glow-color); }
`;

const savedCheckDraw = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;

interface ReviewChipProps {
  chipColor: string;
  chipHoverColor: string;
  chipTextColor: string;
  isClickable: boolean;
  isSaving: boolean;
}

const chipStyleProps = new Set<PropertyKey>(['chipColor', 'chipHoverColor', 'chipTextColor', 'isClickable', 'isSaving']);

export const ReviewChip = styled(ButtonBase, {
  shouldForwardProp: prop => !chipStyleProps.has(prop),
})<ReviewChipProps>(({ chipColor, chipHoverColor, chipTextColor, isClickable, isSaving, theme }) => ({
  '--review-chip-glow-color': alpha(chipColor, 0.6),
  alignItems: 'center',
  animation: isSaving ? `${chipSavePulse} 1200ms ease-in-out infinite` : 'none',
  backgroundColor: chipColor,
  borderRadius: '4px',
  color: chipTextColor,
  cursor: isClickable ? 'pointer' : 'default',
  display: 'inline-flex',
  fontSize: 10,
  fontWeight: 600,
  height: 18,
  lineHeight: `${CHIP_LINE_HEIGHT_PX}px`,
  overflow: 'hidden',
  padding: '2px 8px',
  transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
    duration: 240,
    easing: theme.transitions.easing.easeInOut,
  }),
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: chipHoverColor,
  },
}));

// Sized to the active slot so the chip eases between label widths.
export const ReviewChipContent = styled('span')(({ theme }) => ({
  display: 'inline-block',
  height: CHIP_LINE_HEIGHT_PX,
  overflow: 'hidden',
  position: 'relative',
  transition: theme.transitions.create(['width'], {
    duration: 240,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

const slotTranslateY = (position: NavigationActionContentPosition) => {
  if (position === 'active') {
    return '0px';
  }
  return position === 'next' ? `-${CHIP_LINE_HEIGHT_PX}px` : `${CHIP_LINE_HEIGHT_PX}px`;
};

export const ReviewChipSlot = styled('span', {
  shouldForwardProp: prop => prop !== 'position',
})<{ position: NavigationActionContentPosition }>(({ position, theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: '4px',
  left: '50%',
  opacity: position === 'active' ? 1 : 0,
  position: 'absolute',
  top: '50%',
  transform: `translate(-50%, -50%) translateY(${slotTranslateY(position)})`,
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: 260,
    easing: theme.transitions.easing.easeInOut,
  }),
  whiteSpace: 'nowrap',
}));

export const ReviewChipSavedCheck = styled('svg')({
  display: 'block',
  height: 10,
  overflow: 'visible',
  width: 10,
});

export const ReviewChipSavedCheckPath = styled('path')({
  animation: `${savedCheckDraw} 420ms ease-out 80ms both`,
  fill: 'none',
  stroke: 'currentColor',
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: 2,
});
